import React from 'react';
import { Wrench, Briefcase, Building2, GraduationCap } from 'lucide-react';
import { MatchingPriorities, MatchingPriorityLevel, MATCHING_PRIORITY_LEVELS } from '@/models/job';
import { SegmentedButton } from '@/components/ui/atoms/SegmentedButton';

export const MATCHING_PRIORITY_KEYS: Array<keyof MatchingPriorities> = [
  'yoe',
  'skills_match',
  'sector_experience',
  'degree',
];

const CRITERION_ICONS: Record<keyof MatchingPriorities, React.ReactNode> = {
  skills_match: <Wrench className="h-4 w-4" />,
  yoe: <Briefcase className="h-4 w-4" />,
  sector_experience: <Building2 className="h-4 w-4" />,
  degree: <GraduationCap className="h-4 w-4" />,
};

interface MatchingPrioritySelectorProps {
  value: MatchingPriorities;
  labels: Record<keyof MatchingPriorities, string>;
  priorityLabels: Record<MatchingPriorityLevel, string>;
  onChange: (next: MatchingPriorities) => void;
  readOnly?: boolean;
}

export const MatchingPrioritySelector: React.FC<MatchingPrioritySelectorProps> = ({
  value,
  labels,
  priorityLabels,
  onChange,
  readOnly = false,
}) => {
  const updatePriority = (key: keyof MatchingPriorities, level: MatchingPriorityLevel) => {
    onChange({ ...value, [key]: level });
  };

  return (
    <div className="space-y-4">
      {MATCHING_PRIORITY_KEYS.map((key) => (
        <div key={key} className="space-y-2">
          <span className="flex items-center gap-2 text-sm font-semibold text-dark-invirtus font-body">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-dark-invirtus-soft text-dark-invirtus">
              {CRITERION_ICONS[key]}
            </span>
            {labels[key]}
          </span>

          <SegmentedButton
            value={value[key]}
            onValueChange={(level) => updatePriority(key, level as MatchingPriorityLevel)}
            options={MATCHING_PRIORITY_LEVELS.map((level) => ({
              value: level,
              label: priorityLabels[level],
              disabled: readOnly,
            }))}
            aria-label={labels[key]}
            appearance="raised"
            intent="secondary"
            size="sm"
            equalWidth
            rounded="full"
          />
        </div>
      ))}
    </div>
  );
};
